import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type FlashHotelCatalogConfig,
  type RewardsCatalog,
  type RewardsPoolView,
  type SocieteRewardAdminAction,
  fetchCatalogs,
  getRewardsPool,
  listRewardsPools,
  listSocieteRewardOverrides,
  patchPackageSocieteReward,
  updateRewardsCatalogConfig,
  upsertRewardsPoolCap,
} from '~/services/LuxLoyalty';

import { rewardsQueryKeys } from './rewardsQueryKeys';

/**
 * Fetches an offer's Société flash-hotel allocation state (pool cap, reserved/redeemed counts).
 * Pass `null` to disable the query (e.g. before an offer is selected).
 */
export function useRewardsPool(offerId: string | null) {
  return useQuery({
    queryKey: offerId ? rewardsQueryKeys.pool(offerId) : [...rewardsQueryKeys.all, 'pool', 'disabled'],
    queryFn: () => getRewardsPool(offerId as string),
    enabled: Boolean(offerId),
  });
}

/**
 * Sets or clears (via `null`) an offer's flash-hotel allocation cap.
 * On success, seeds the pool cache with the fresh view returned by the API instead of refetching,
 * and invalidates every `pools` listing page (the admin "Pools" tab) so a cap edit is reflected
 * there too — that list can't be seeded in place since the edited offer's row (and its position in
 * the newest-updated-first order) depends on the full server-side listing.
 */
export function useUpsertPoolCap(offerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ totalQuantity }: { totalQuantity: number | null }) => upsertRewardsPoolCap(offerId, totalQuantity),
    onSuccess: async (view: RewardsPoolView) => {
      queryClient.setQueryData(rewardsQueryKeys.pool(offerId), view);
      await queryClient.invalidateQueries({ queryKey: [...rewardsQueryKeys.all, 'pools'] });
    },
  });
}

/** Fetches one page of every Société flash-hotel reward pool (admin listing), newest-updated first. */
export function useRewardsPools(params: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: rewardsQueryKeys.pools(params),
    queryFn: () => listRewardsPools(params),
  });
}

/**
 * Fetches the active hotel_flash reward catalog (there should only ever be one). Returns the newest
 * ACTIVE catalog by `createdAt`, or `null` when none exists.
 */
export function useFlashHotelCatalog() {
  return useQuery({
    queryKey: rewardsQueryKeys.flashConfig(),
    queryFn: async () => {
      const catalogs = await fetchCatalogs({ itemType: 'hotel_flash', status: 'ACTIVE' });
      return pickNewestCatalog(catalogs);
    },
  });
}

/** Sorts defensively by `createdAt` desc and returns the newest catalog, or `null` when the list is empty. */
function pickNewestCatalog(catalogs: Array<RewardsCatalog>): RewardsCatalog | null {
  if (catalogs.length === 0) {
    return null;
  }
  const sorted = [...catalogs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return sorted[0];
}

/**
 * Replaces the hotel_flash catalog's pricing/display config constants.
 * On success writes the saved config into the cached catalog, so pages show it straight away,
 * then invalidates the flash config query to pick up anything else the save changed.
 */
export function useUpdateCatalogConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ catalogId, config }: { catalogId: string; config: FlashHotelCatalogConfig }) =>
      updateRewardsCatalogConfig(catalogId, config),
    onSuccess: async (result, { catalogId }) => {
      queryClient.setQueryData<RewardsCatalog | null>(rewardsQueryKeys.flashConfig(), (cached) =>
        cached?.id === catalogId ? { ...cached, config: result.config } : cached,
      );
      await queryClient.invalidateQueries({ queryKey: rewardsQueryKeys.flashConfig() });
    },
  });
}

/** Fetches one page of manually overridden Société reward packages (audit list). */
export function useSocieteRewardOverrides(params: { limit: number; offset: number }) {
  return useQuery({
    queryKey: rewardsQueryKeys.societeOverrides(params),
    queryFn: () => listSocieteRewardOverrides(params),
  });
}

/**
 * Applies an admin action (override / clear_override / disable / enable) to one package's Société reward.
 * On success invalidates every `societeOverrides` page so the audit list reflects the change. The
 * resolved `SocieteRewardPatchResult` is returned to the caller for updating any in-view summary.
 */
export function usePatchSocieteReward(offerSfId: string, packageId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (action: SocieteRewardAdminAction) => patchPackageSocieteReward(offerSfId, packageId, action),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...rewardsQueryKeys.all, 'societe-overrides'] });
    },
  });
}
